/**
 * GroupListView — "By Group" view for folder permissions.
 *
 * Shows workspace groups, expandable to see which folders each group
 * has access to with editable roles.
 */
import {
  Button,
  Loading,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  RowInput,
  Scrollable,
} from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { FolderApiService } from '@affine/core/modules/folder-api';
import { ObjectPermissionMutationService } from '@affine/core/modules/permissions';
import { objectRoleLabel } from '@affine/core/modules/permissions/utils/object-role-labels';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { FolderRole, getGroupFolderRolesQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  DeleteIcon,
  FolderIcon,
  PlusIcon,
  SearchIcon,
} from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSettingsOfflineIncompleteLabel } from '../../shared/settings-offline-status';
import * as styles from '../permissions/styles.css';
import {
  SettingsEntityCard,
  SettingsEntityMessage,
} from '../shared/settings-entity';
import * as entityStyles from '../shared/settings-entity.css';
import { SettingsGroupSummary } from '../shared/settings-identity';
import * as navStyles from '../shared/settings-navigation.css';

interface GroupInfo {
  id: string;
  name: string;
  description?: string | null;
}

interface GroupFolderRole {
  id: string;
  folderId: string;
  role: string;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
}

interface GroupListViewProps {
  searchQuery: string;
}

const FOLDER_GRANTABLE_ROLES = [
  FolderRole.Admin,
  FolderRole.Editor,
  FolderRole.Reader,
];

const getAreaBlockedNotice = (
  t: ReturnType<typeof useI18n>,
  entry: GroupFolderRole
) => {
  if (!entry.isAreaBlocked) {
    return null;
  }
  const areaName = entry.areaBoundaryRootName;
  if (entry.areaBoundaryKind === 'private' && areaName) {
    return t['com.streamlain.folder-share.area-blocked.private']({ areaName });
  }
  if (entry.areaBoundaryKind === 'teamspace' && areaName) {
    return t['com.streamlain.folder-share.area-blocked.teamspace']({
      areaName,
    });
  }
  return t['com.streamlain.folder-share.area-blocked.generic']();
};

export const FolderGroupListView = ({ searchQuery }: GroupListViewProps) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const folderApiService = useService(FolderApiService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;

  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!backendAvailable) {
        setIsLoading(false);
        return;
      }
      try {
        const result = await folderApiService.getGroups(workspaceId);
        setGroups(
          result.map(g => ({
            id: g.id,
            name: g.name,
            description: g.description,
          }))
        );
      } catch (err) {
        console.debug('Failed to load groups:', err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [backendAvailable, folderApiService, workspaceId]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;
    const q = searchQuery.toLowerCase();
    return groups.filter(g => g.name.toLowerCase().includes(q));
  }, [groups, searchQuery]);

  const handleGroupClick = useCallback((groupId: string) => {
    setExpandedGroupId(prev => (prev === groupId ? null : groupId));
  }, []);

  if (isLoading && backendAvailable) {
    return (
      <SettingsEntityMessage>
        <Loading size={20} />
        <span>
          {t['com.streamlain.settings.workspace.permissions.loading']?.() ??
            'Loading...'}
        </span>
      </SettingsEntityMessage>
    );
  }

  if (!backendAvailable && groups.length === 0) {
    return <SettingsEntityMessage>{offlineLabel}</SettingsEntityMessage>;
  }

  if (filteredGroups.length === 0) {
    return (
      <SettingsEntityMessage>
        {t[
          'com.streamlain.settings.workspace.permissions.no-groups-found'
        ]?.() ?? 'No groups in this workspace.'}
      </SettingsEntityMessage>
    );
  }

  return (
    <div>
      {filteredGroups.map(group => (
        <GroupFolderListItem
          key={group.id}
          group={group}
          isExpanded={expandedGroupId === group.id}
          onClick={() => handleGroupClick(group.id)}
        />
      ))}
    </div>
  );
};

// =============================================================================
// Group List Item
// =============================================================================

const GroupFolderListItem = ({
  group,
  isExpanded,
  onClick,
}: {
  group: GroupInfo;
  isExpanded: boolean;
  onClick: () => void;
}) => {
  return (
    <SettingsEntityCard>
      <SettingsGroupSummary
        expanded={isExpanded}
        onToggle={onClick}
        group={group}
      />
      {isExpanded && (
        <div className={entityStyles.expanded}>
          <GroupFoldersList groupId={group.id} />
        </div>
      )}
    </SettingsEntityCard>
  );
};

// =============================================================================
// Group Folders List (editable roles)
// =============================================================================

const GroupFoldersList = ({ groupId }: { groupId: string }) => {
  const t = useI18n();
  const serverService = useService(WorkspaceServerService);
  const folderApiService = useService(FolderApiService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;

  const [folderRoles, setFolderRoles] = useState<GroupFolderRole[]>([]);
  const [allFolders, setAllFolders] = useState<
    { id: string; name: string; isWorkspaceRoot: boolean }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Add state
  const [showAdd, setShowAdd] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<FolderRole>(
    FolderRole.Editor
  );

  const loadData = useCallback(async () => {
    if (!serverService.server) {
      setIsLoading(false);
      return;
    }
    try {
      const [rolesResult, foldersResult] = await Promise.all([
        serverService.server.gql({
          query: getGroupFolderRolesQuery,
          variables: { workspaceId, groupId },
        }),
        folderApiService.getFolders(workspaceId),
      ]);
      setFolderRoles(
        (rolesResult.groupFolderRoles ?? []).map(
          (r: {
            id: string;
            folderId: string;
            role: string;
            isAreaBlocked?: boolean | null;
            areaBoundaryKind?: string | null;
            areaBoundaryRootFolderId?: string | null;
            areaBoundaryRootName?: string | null;
          }) => ({
            id: r.id,
            folderId: r.folderId,
            role: r.role,
            isAreaBlocked: r.isAreaBlocked ?? false,
            areaBoundaryKind: r.areaBoundaryKind ?? null,
            areaBoundaryRootFolderId: r.areaBoundaryRootFolderId ?? null,
            areaBoundaryRootName: r.areaBoundaryRootName ?? null,
          })
        )
      );
      setAllFolders(
        foldersResult.map(f => ({
          id: f.id,
          name: f.name || 'Untitled',
          isWorkspaceRoot: f.isWorkspaceRoot,
        }))
      );
    } catch (err) {
      console.debug('Failed to load group folder roles:', err);
    } finally {
      setIsLoading(false);
    }
  }, [serverService.server, folderApiService, workspaceId, groupId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const folderNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of allFolders) m.set(f.id, f.name);
    return m;
  }, [allFolders]);

  const availableFolders = useMemo(() => {
    if (!showAdd) return [];
    const grantedIds = new Set(folderRoles.map(r => r.folderId));
    const folders = allFolders
      .filter(f => !grantedIds.has(f.id) && !f.isWorkspaceRoot)
      .map(f => ({ id: f.id, name: f.name }));
    const q = addSearch.toLowerCase();
    return q ? folders.filter(f => f.name.toLowerCase().includes(q)) : folders;
  }, [showAdd, folderRoles, allFolders, addSearch]);

  const handleGrant = useCallback(
    async (folderId: string) => {
      setIsUpdating(true);
      try {
        await objectPermissionMutation.execute({
          action: 'grant',
          principal: { id: groupId, kind: 'group' },
          role: selectedRole,
          target: { id: folderId, kind: 'folder' },
        });
        setFolderRoles(prev => [
          ...prev.filter(role => role.folderId !== folderId),
          {
            id: `direct:${folderId}:${groupId}`,
            folderId,
            role: selectedRole,
            isAreaBlocked: false,
            areaBoundaryKind: null,
            areaBoundaryRootFolderId: null,
            areaBoundaryRootName: null,
          },
        ]);
        void loadData();
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.granted']?.() ??
            'Permission granted',
        });
        setShowAdd(false);
        setAddSearch('');
      } catch (err) {
        console.error('Failed to grant folder group access:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.grant-failed'
            ]?.() ?? 'Failed to grant permission',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [groupId, loadData, objectPermissionMutation, selectedRole, t]
  );

  const handleUpdateRole = useCallback(
    async (folderId: string, newRole: string) => {
      setIsUpdating(true);
      try {
        await objectPermissionMutation.execute({
          action: 'update',
          principal: { id: groupId, kind: 'group' },
          role: newRole,
          target: { id: folderId, kind: 'folder' },
        });
        setFolderRoles(prev =>
          prev.map(role =>
            role.folderId === folderId ? { ...role, role: newRole } : role
          )
        );
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.updated']?.() ??
            'Permission updated',
        });
      } catch (err) {
        console.error('Failed to update group folder role:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.update-failed'
            ]?.() ?? 'Failed to update permission',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [groupId, objectPermissionMutation, t]
  );

  const handleRevoke = useCallback(
    async (folderId: string) => {
      setIsUpdating(true);
      try {
        await objectPermissionMutation.execute({
          action: 'revoke',
          principal: { id: groupId, kind: 'group' },
          target: { id: folderId, kind: 'folder' },
        });
        setFolderRoles(prev => prev.filter(role => role.folderId !== folderId));
        notify.success({
          title:
            t['com.streamlain.settings.workspace.permissions.revoked']?.() ??
            'Access removed',
        });
      } catch (err) {
        console.error('Failed to revoke group folder role:', err);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.permissions.revoke-failed'
            ]?.() ?? 'Failed to remove access',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [groupId, objectPermissionMutation, t]
  );

  const getRoleLabel = useCallback(
    (role: string) => objectRoleLabel(t, role),
    [t]
  );

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Loading size={16} />
      </div>
    );
  }

  return (
    <div className={styles.editorContainer}>
      <div className={styles.editorHeader}>
        <span className={styles.editorLabel}>
          {t[
            'com.streamlain.settings.workspace.folders.user-folder-access'
          ]?.() ?? 'Folder access'}
        </span>
        <Button
          variant="plain"
          prefix={<PlusIcon />}
          onClick={() => setShowAdd(!showAdd)}
          disabled={isUpdating}
        >
          {t['com.streamlain.settings.workspace.permissions.add-user']?.() ??
            'Add'}
        </Button>
      </div>

      {showAdd && (
        <>
          <div className={styles.inviteContainer}>
            <div className={styles.inviteSearch}>
              <SearchIcon className={styles.searchIcon} />
              <RowInput
                value={addSearch}
                onChange={setAddSearch}
                placeholder={
                  t[
                    'com.streamlain.settings.workspace.folders.search-folders'
                  ]?.() ?? 'Search folders...'
                }
                className={styles.searchInputField}
              />
            </div>
            <Menu
              items={FOLDER_GRANTABLE_ROLES.map(role => (
                <MenuItem
                  key={role}
                  selected={role === selectedRole}
                  onSelect={() => setSelectedRole(role)}
                >
                  {getRoleLabel(role)}
                </MenuItem>
              ))}
              contentOptions={{ align: 'end' }}
            >
              <MenuTrigger className={navStyles.roleMenuTrigger}>
                {getRoleLabel(selectedRole)}
              </MenuTrigger>
            </Menu>
          </div>

          {availableFolders.length > 0 && (
            <Scrollable.Root className={styles.inviteMembersList}>
              <Scrollable.Viewport>
                {availableFolders.slice(0, 20).map(folder => (
                  <div
                    key={folder.id}
                    className={styles.inviteMemberRow}
                    onClick={() => {
                      void handleGrant(folder.id);
                    }}
                  >
                    <FolderIcon
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>{folder.name}</div>
                    </div>
                  </div>
                ))}
              </Scrollable.Viewport>
              <Scrollable.Scrollbar />
            </Scrollable.Root>
          )}

          {availableFolders.length === 0 && addSearch && (
            <div className={styles.noMembersFound}>
              {t[
                'com.streamlain.settings.workspace.permissions.no-docs-found'
              ]?.() ?? 'No folders found'}
            </div>
          )}
        </>
      )}

      {folderRoles.length === 0 ? (
        <div className={styles.emptyPermissions}>
          {t[
            'com.streamlain.settings.workspace.permissions.no-granted-users'
          ]?.() ?? 'No folder permissions for this group.'}
        </div>
      ) : (
        <div className={styles.grantedUsersList}>
          {folderRoles.map(fr => {
            const areaBlockedNotice = getAreaBlockedNotice(t, fr);

            return (
              <div
                key={fr.folderId}
                className={styles.permissionRowWrapper}
                data-area-blocked={fr.isAreaBlocked ? 'true' : undefined}
              >
                <div
                  className={`${styles.permissionRow} ${
                    fr.isAreaBlocked ? styles.areaBlockedPermissionRow : ''
                  }`}
                >
                  <div className={styles.permissionUser}>
                    <FolderIcon
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    <div className={styles.listItemContent}>
                      <div className={styles.listItemTitle}>
                        {folderNameMap.get(fr.folderId) ?? fr.folderId}
                      </div>
                    </div>
                  </div>

                  <Menu
                    items={FOLDER_GRANTABLE_ROLES.map(role => (
                      <MenuItem
                        key={role}
                        selected={role === fr.role}
                        onSelect={() => {
                          void handleUpdateRole(fr.folderId, role);
                        }}
                      >
                        {getRoleLabel(role)}
                      </MenuItem>
                    ))}
                    contentOptions={{ align: 'end' }}
                  >
                    <MenuTrigger
                      className={navStyles.roleMenuTrigger}
                      disabled={isUpdating}
                    >
                      {getRoleLabel(fr.role)}
                    </MenuTrigger>
                  </Menu>

                  <Button
                    variant="plain"
                    className={styles.removeButton}
                    onClick={() => {
                      void handleRevoke(fr.folderId);
                    }}
                    disabled={isUpdating}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
                {areaBlockedNotice ? (
                  <div className={styles.areaBlockedNotice}>
                    {areaBlockedNotice}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
